import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Paper,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { useInput, useTranslate } from 'react-admin';

interface QueueSchedule {
  dayOfWeek: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  startTime: string;
  endTime: string;
  timezone?: string;
}

const DAYS_OF_WEEK = [
  { value: 0, label: 'schedule.days.sunday' },
  { value: 1, label: 'schedule.days.monday' },
  { value: 2, label: 'schedule.days.tuesday' },
  { value: 3, label: 'schedule.days.wednesday' },
  { value: 4, label: 'schedule.days.thursday' },
  { value: 5, label: 'schedule.days.friday' },
  { value: 6, label: 'schedule.days.saturday' },
];

const COMMON_TIMEZONES = [
  'America/Sao_Paulo',
  'America/New_York',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Paris',
  'Asia/Tokyo',
  'UTC',
];

interface DaySchedule {
  enabled: boolean;
  startTime: string;
  endTime: string;
}

interface ScheduleEditorProps {
  source: string;
  label?: string;
}

export const ScheduleEditor = ({ source, label }: ScheduleEditorProps) => {
  const translate = useTranslate();
  const {
    field: { value, onChange },
  } = useInput({ source });

  const [timezone, setTimezone] = useState<string>('America/Sao_Paulo');
  const [daySchedules, setDaySchedules] = useState<Record<number, DaySchedule>>(
    () => {
      const initial: Record<number, DaySchedule> = {};
      DAYS_OF_WEEK.forEach((day) => {
        initial[day.value] = {
          enabled: false,
          startTime: '09:00',
          endTime: '18:00',
        };
      });
      return initial;
    }
  );

  // Initialize from existing value
  useEffect(() => {
    if (value && Array.isArray(value)) {
      const newSchedules: Record<number, DaySchedule> = {};
      DAYS_OF_WEEK.forEach((day) => {
        newSchedules[day.value] = {
          enabled: false,
          startTime: '09:00',
          endTime: '18:00',
        };
      });

      value.forEach((schedule: QueueSchedule) => {
        newSchedules[schedule.dayOfWeek] = {
          enabled: true,
          startTime: schedule.startTime,
          endTime: schedule.endTime,
        };
        if (schedule.timezone) {
          setTimezone(schedule.timezone);
        }
      });

      setDaySchedules(newSchedules);
    }
  }, []);

  // Convert to API format and update
  const updateValue = (newSchedules: Record<number, DaySchedule>) => {
    const schedules: QueueSchedule[] = [];
    Object.entries(newSchedules).forEach(([dayStr, schedule]) => {
      if (schedule.enabled) {
        schedules.push({
          dayOfWeek: parseInt(dayStr) as 0 | 1 | 2 | 3 | 4 | 5 | 6,
          startTime: schedule.startTime,
          endTime: schedule.endTime,
          timezone,
        });
      }
    });
    onChange(schedules.length > 0 ? schedules : null);
  };

  const handleDayToggle = (day: number) => {
    const newSchedules = {
      ...daySchedules,
      [day]: {
        ...daySchedules[day],
        enabled: !daySchedules[day].enabled,
      },
    };
    setDaySchedules(newSchedules);
    updateValue(newSchedules);
  };

  const handleTimeChange = (
    day: number,
    field: 'startTime' | 'endTime',
    value: string
  ) => {
    const newSchedules = {
      ...daySchedules,
      [day]: {
        ...daySchedules[day],
        [field]: value,
      },
    };
    setDaySchedules(newSchedules);
    updateValue(newSchedules);
  };

  const handleTimezoneChange = (newTimezone: string) => {
    setTimezone(newTimezone);
    // Re-update value with new timezone
    const schedules: QueueSchedule[] = [];
    Object.entries(daySchedules).forEach(([dayStr, schedule]) => {
      if (schedule.enabled) {
        schedules.push({
          dayOfWeek: parseInt(dayStr) as 0 | 1 | 2 | 3 | 4 | 5 | 6,
          startTime: schedule.startTime,
          endTime: schedule.endTime,
          timezone: newTimezone,
        });
      }
    });
    onChange(schedules.length > 0 ? schedules : null);
  };

  return (
    <Paper sx={{ p: 2, mb: 2 }} variant="outlined">
      <Typography variant="subtitle1" gutterBottom>
        {label || translate('resources.ticket-queue.fields.schedules')}
      </Typography>

      <FormControl fullWidth sx={{ mb: 2 }} size="small">
        <InputLabel>{translate('schedule.timezone')}</InputLabel>
        <Select
          value={timezone}
          label={translate('schedule.timezone')}
          onChange={(e) => handleTimezoneChange(e.target.value)}
        >
          {COMMON_TIMEZONES.map((tz) => (
            <MenuItem key={tz} value={tz}>
              {tz}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Stack spacing={1}>
        {DAYS_OF_WEEK.map((day) => (
          <Box
            key={day.value}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              py: 0.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={daySchedules[day.value].enabled}
                  onChange={() => handleDayToggle(day.value)}
                  size="small"
                />
              }
              label={translate(day.label)}
              sx={{ minWidth: 120 }}
            />

            {daySchedules[day.value].enabled && (
              <>
                <TextField
                  type="time"
                  size="small"
                  label={translate('schedule.start')}
                  value={daySchedules[day.value].startTime}
                  onChange={(e) =>
                    handleTimeChange(day.value, 'startTime', e.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 130 }}
                />
                <Typography>-</Typography>
                <TextField
                  type="time"
                  size="small"
                  label={translate('schedule.end')}
                  value={daySchedules[day.value].endTime}
                  onChange={(e) =>
                    handleTimeChange(day.value, 'endTime', e.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 130 }}
                />
              </>
            )}
          </Box>
        ))}
      </Stack>
    </Paper>
  );
};
